import { useMemo } from 'react'
import { useSnapshot } from 'valtio'
import type { OpCreditInput } from '@/shared/lib/op-registry'
import { calculatePointConsume } from '../lib/point-rule-calculator'
import { pointRuleConfigStore } from './point-rule-config-store'

interface UseCreditCostResult {
  credit: number
  isLoading: boolean
}

interface UseCreditCostByInputOptions {
  input: OpCreditInput | OpCreditInput[] | null
}

function normalizeCreditInput(input: OpCreditInput | OpCreditInput[] | null) {
  if (!input) {
    return []
  }

  return Array.isArray(input) ? input : [input]
}

function isCreditLoading(input: OpCreditInput | OpCreditInput[] | null, status: string) {
  return Boolean(input) && (status === 'idle' || status === 'loading')
}

export function useCreditCostByInput({ input }: UseCreditCostByInputOptions): UseCreditCostResult {
  const { pointRuleConfig, status } = useSnapshot(pointRuleConfigStore)

  const credit = useMemo(() => {
    if (!input || status !== 'ready') {
      return 0
    }

    return normalizeCreditInput(input).reduce((total, currentInput) => {
      return total + calculatePointConsume({
        payload: {
          key: currentInput.key,
          data: currentInput.data,
        },
        config: pointRuleConfig,
      })
    }, 0)
  }, [input, pointRuleConfig, status])

  return {
    credit,
    isLoading: isCreditLoading(input, status),
  }
}
